import { useUser, useClerk } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PLAN_LABELS = {
  free: { name: 'Free', color: 'var(--text-3)', bg: 'var(--bg-3)' },
  pro:  { name: 'Pro',  color: 'var(--accent)',  bg: 'var(--accent-dark)' },
};

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-3)', fontFamily: 'var(--mono)', background: 'var(--bg-3)', padding: '2px 10px', borderRadius: 4 }}>{value}</span>
    </div>
  );
}

export default function ProfilePage({ onNavigate }) {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const [subscription, setSubscription] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetch(`${BASE}/api/payments/status`, {
        headers: { 'x-user-id': user.id },
      }).then(r => r.json()).then(setSubscription).catch(console.error);
    }
  }, [user?.id]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    setCancelling(true);
    try {
      await fetch(`${BASE}/api/payments/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
      });
      setSubscription(prev => ({ ...prev, status: 'cancelled' }));
    } catch (err) {
      console.error(err);
    } finally {
      setCancelling(false);
    }
  };

  const initials = user?.fullName
    ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.firstName?.[0]?.toUpperCase() || 'CM';

  const plan = subscription?.plan || 'free';
  const planInfo = PLAN_LABELS[plan] || PLAN_LABELS.free;

  return (
    <div className="page fade-in">
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Profile</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Manage your account</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1.5rem', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 16, marginBottom: '1.25rem' }}>
        {user?.imageUrl ? (
          <img src={user.imageUrl} alt={user.fullName} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid var(--border-2)' }} />
        ) : (
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#000', fontFamily: 'var(--mono)', flexShrink: 0, boxShadow: '0 0 20px rgba(0,255,136,0.2)' }}>
            {initials}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{user?.fullName || user?.firstName || 'Your Account'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 8 }}>{user?.primaryEmailAddress?.emailAddress}</div>
          <button className="btn btn-outline btn-sm" onClick={() => openUserProfile()}>✎ Edit Profile</button>
        </div>
      </div>

      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>👤 Account Details</span>
        </div>
        <div style={{ padding: '0 1.25rem' }}>
          <InfoRow label="Full Name" value={user?.fullName || '—'} />
          <InfoRow label="Email" value={user?.primaryEmailAddress?.emailAddress || '—'} />
          <InfoRow label="Account ID" value={user?.id?.slice(0, 16) + '...' || '—'} />
          <InfoRow label="Member since" value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'} />
        </div>
      </div>

      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>💎 Plan</span>
        </div>
        <div style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: plan !== 'free' ? '1rem' : 0 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                {planInfo.name} Plan
                <span style={{ fontSize: 11, fontFamily: 'var(--mono)', background: planInfo.bg, color: planInfo.color, border: `1px solid ${planInfo.color}33`, padding: '2px 8px', borderRadius: 20 }}>
                  {subscription?.status?.toUpperCase() || 'ACTIVE'}
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                {plan === 'free' && '3 meetings/month · Basic AI analysis'}
                {plan === 'pro' && 'Unlimited meetings · Full AI + all features'}
              </div>
            </div>
            {plan === 'free' ? (
              <button className="btn btn-primary btn-sm" onClick={() => onNavigate('pricing')}>↑ Upgrade</button>
            ) : (
              <button className="btn btn-outline btn-sm" onClick={() => onNavigate('pricing')}>Manage Plan</button>
            )}
          </div>
          {plan !== 'free' && subscription?.status !== 'cancelled' && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Monthly subscription · Cancel anytime</div>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 11 }} onClick={handleCancel} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel subscription'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ background: 'var(--bg-2)', border: '1px solid rgba(255,77,106,0.2)', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid rgba(255,77,106,0.15)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--red)' }}>⚠ Account</span>
        </div>
        <div style={{ padding: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Sign out</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>You will be returned to the home page</div>
          </div>
          <button className="btn btn-danger btn-sm" onClick={() => signOut()}>Sign out</button>
        </div>
      </div>
    </div>
  );
}
